/**
 * Loads GSC meta always. Loads GA4 with Consent Mode v2 (cookieless before Accept).
 * Loads Microsoft Clarity / Plausible only after analytics consent.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { repairSearch, trackingSearch } from '../utils/queryString';
import {
  flushQueuedGaEvents,
  parseStripeReturn,
  stripStripeReturnSearch,
  trackPurchaseOnce,
} from '../utils/analytics';
import { captureAttributionFromLocation } from '../utils/attribution';
import {
  applyStoredConsentUpdate,
  ensureGaConsentShell,
  GA_ID,
  updateGaConsent,
} from '../utils/consentMode';
import {
  COOKIE_CONSENT_EVENT,
  CONSENT_ACCEPTED,
  CONSENT_REJECTED,
  hasAnalyticsConsent,
} from '../utils/cookieConsent';
import { syncClarityForPath } from '../utils/clarity';

const PLAUSIBLE_DOMAIN = (process.env.REACT_APP_PLAUSIBLE_DOMAIN || '').trim();
const GSC_TOKEN = (process.env.REACT_APP_GSC_VERIFICATION || '').trim();
const CLARITY_ID = (process.env.REACT_APP_CLARITY_PROJECT_ID || '').trim();

let plausibleBootstrapped = false;
let gscBootstrapped = false;

const ensureGscMeta = () => {
  if (!GSC_TOKEN || gscBootstrapped) return;
  gscBootstrapped = true;
  let el = document.head.querySelector('meta[name="google-site-verification"]');
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', 'google-site-verification');
    document.head.appendChild(el);
  }
  el.setAttribute('content', GSC_TOKEN);
};

const ensurePlausible = () => {
  if (!PLAUSIBLE_DOMAIN || plausibleBootstrapped || !hasAnalyticsConsent()) return;
  plausibleBootstrapped = true;
  const script = document.createElement('script');
  script.defer = true;
  script.setAttribute('data-domain', PLAUSIBLE_DOMAIN);
  script.src = 'https://plausible.io/js/script.js';
  document.head.appendChild(script);
};

const trackGaPageview = (path) => {
  if (!GA_ID || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
};

const bootConsentedTrackers = (pathname) => {
  ensurePlausible();
  syncClarityForPath(pathname, { projectId: CLARITY_ID });
};

/**
 * Mount once inside the router. No UI.
 */
const Analytics = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, refreshSubscription } = useAuth();
  const stripeHandledRef = useRef('');

  useEffect(() => {
    ensureGscMeta();
    ensureGaConsentShell();
    applyStoredConsentUpdate();
    flushQueuedGaEvents();
    bootConsentedTrackers(location.pathname);

    const onConsent = (event) => {
      const detail = event.detail;
      const analyticsOn =
        typeof detail === 'object' && detail !== null
          ? Boolean(detail.analytics)
          : detail === CONSENT_ACCEPTED;
      updateGaConsent(analyticsOn ? CONSENT_ACCEPTED : CONSENT_REJECTED);
      bootConsentedTrackers(location.pathname);
      flushQueuedGaEvents();
      const path = `${location.pathname}${trackingSearch(location.search)}`;
      trackGaPageview(path);
    };
    window.addEventListener(COOKIE_CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onConsent);
  }, [location.pathname, location.search]);

  useEffect(() => {
    captureAttributionFromLocation(location.search, location.pathname);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const repaired = repairSearch(location.search);
    if (repaired !== (location.search || '')) {
      navigate(
        {
          pathname: location.pathname,
          search: repaired,
          hash: location.hash,
        },
        { replace: true }
      );
      return;
    }

    const stripe = parseStripeReturn(location.search);
    if (stripe.success || stripe.canceled || stripe.billingReturn) {
      const handleKey = `${location.pathname}${location.search}`;
      if (stripeHandledRef.current !== handleKey) {
        stripeHandledRef.current = handleKey;
        if (stripe.success) {
          trackPurchaseOnce({
            sessionId: stripe.sessionId,
            kind: stripe.kind,
          });
          if (user) {
            refreshSubscription();
          }
          toast.success(t('pricing.toastSuccess'), { toastId: 'pricing-success' });
        } else if (stripe.canceled) {
          toast.info(t('pricing.toastCanceled'), { toastId: 'pricing-canceled' });
        } else if (stripe.billingReturn) {
          if (user) {
            refreshSubscription();
          }
          toast.info(t('pricing.billingReturned'), {
            toastId: 'pricing-billing-return',
          });
        }
      }
      const nextSearch = stripStripeReturnSearch(location.search);
      if (nextSearch !== (location.search || '')) {
        navigate(
          {
            pathname: location.pathname,
            search: nextSearch,
            hash: location.hash,
          },
          { replace: true }
        );
        return;
      }
    }

    const path = `${location.pathname}${trackingSearch(location.search)}`;
    trackGaPageview(path);
    syncClarityForPath(location.pathname, { projectId: CLARITY_ID });
  }, [
    location.pathname,
    location.search,
    location.hash,
    navigate,
    t,
    user,
    refreshSubscription,
  ]);

  return null;
};

export default Analytics;
